/**
 * PocArchiveTab — Story 1.5 (PoC-Historienarchiv UI, HIP-149)
 *
 * Referenz: claude-design-import/hotspot-detail.html (Panel „PoC-Archiv").
 *  - Archiv-Banner (AC-1): „Proof-of-Coverage wurde im Juli 2026 netzwerkweit
 *    eingestellt (HIP-149)…" — i18n DE/EN.
 *  - Eingefrorene Kacheln in Legacy-Farbgebung (AC-2) mit historischen
 *    Witness-/Reward-Werten aus GET /api/v2/hotspots/{id}/poc_archive.
 *  - CSV-Export-Button (AC-3) über den BE-Export-Endpunkt
 *    GET /api/v2/hotspots/{id}/poc_archive/export.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Download } from 'lucide-react'
import { toast } from '../../components/Toast/useToast'
import { formatNumber } from '../../components/ui'
import { activeBcp47 } from '../../lib/locale'
import {
  useHotspotPocArchive,
  downloadPocArchiveCsv,
  type HotspotPocArchive,
} from './useHotspotPocArchive'
import './hotspot-detail-tabs.css'

type Props = {
  hotspotId: string
}

function formatDate(iso: string | null | undefined, opts?: Intl.DateTimeFormatOptions): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(activeBcp47(), opts ?? { dateStyle: 'medium' }).format(d)
}

function PocTiles({ archive }: { archive: HotspotPocArchive }) {
  const { t } = useTranslation('hotspotDetailTabs')
  const witnesses = archive.witnesses
  const totals = archive.totals

  return (
    <div className="htdt-poc-tiles" data-testid="poc-archive-tiles">
      <div className="htdt-poc-tile" data-testid="poc-tile-beacons">
        <div className="v">{formatNumber(archive.beacons_sent ?? 0, 0)}</div>
        <div className="l">{t('poc.tiles.beaconsSent')}</div>
        <div className="n">{t('poc.tiles.beaconsSentNote')}</div>
      </div>
      <div className="htdt-poc-tile" data-testid="poc-tile-witnesses">
        <div className="v">{formatNumber(witnesses?.total ?? 0, 0)}</div>
        <div className="l">{t('poc.tiles.witnessesTotal')}</div>
        <div className="n">
          {t('poc.tiles.witnessesTotalNote', {
            valid: formatNumber(witnesses?.valid ?? 0, 0),
          })}
        </div>
      </div>
      <div className="htdt-poc-tile" data-testid="poc-tile-rewards">
        <div className="v">{formatNumber(totals?.reward ?? 0, 2)}</div>
        <div className="l">{t('poc.tiles.rewardTotal')}</div>
        <div className="n">
          {t('poc.tiles.rewardTotalNote', {
            beacon: formatNumber(totals?.beacon_reward ?? 0, 2),
            witness: formatNumber(totals?.witness_reward ?? 0, 2),
          })}
        </div>
      </div>
      <div className="htdt-poc-tile" data-testid="poc-tile-reward-days">
        <div className="v">{formatNumber(totals?.reward_days ?? 0, 0)}</div>
        <div className="l">{t('poc.tiles.rewardDays')}</div>
        <div className="n">{t('poc.tiles.rewardDaysNote')}</div>
      </div>
    </div>
  )
}

function LastRewardsTable({ rows }: { rows: NonNullable<HotspotPocArchive['last_rewards']> }) {
  const { t } = useTranslation('hotspotDetailTabs')

  if (rows.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 13, marginTop: 12 }} data-testid="poc-last-rewards-empty">
        {t('poc.lastRewards.empty')}
      </p>
    )
  }

  return (
    <div className="htdt-table-scroll">
      <table className="htdt-poc-table" data-testid="poc-last-rewards-table">
        <caption className="ui-sr-only">{t('poc.lastRewards.heading')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('poc.lastRewards.colDate')}</th>
            <th scope="col">{t('poc.lastRewards.colReward')}</th>
            <th scope="col">{t('poc.lastRewards.colBeacon')}</th>
            <th scope="col">{t('poc.lastRewards.colWitness')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.date ?? i}>
              <td>{formatDate(r.date)}</td>
              <td className="num">{r.reward != null ? formatNumber(r.reward, 4) : '—'}</td>
              <td className="num">{r.beacon_reward != null ? formatNumber(r.beacon_reward, 4) : '—'}</td>
              <td className="num">{r.witness_reward != null ? formatNumber(r.witness_reward, 4) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function PocArchiveTab({ hotspotId }: Props) {
  const { t } = useTranslation('hotspotDetailTabs')
  const { data: archive, isLoading, isError } = useHotspotPocArchive(hotspotId)
  const [exporting, setExporting] = useState(false)

  async function handleExport() {
    setExporting(true)
    try {
      await downloadPocArchiveCsv(hotspotId)
      toast.success(t('poc.export.successToast'))
    } catch {
      toast.error(t('poc.export.errorToast'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div data-testid="poc-archive-tab">
      {/* AC-1: Archiv-Banner */}
      <div className="legacy-banner" style={{ marginBottom: 18 }} data-testid="poc-archive-banner">
        <span className="ic" aria-hidden="true">
          <AlertTriangle size={18} />
        </span>
        <div>
          <strong>{t('poc.banner.headline')}</strong>
          <br />
          {t('poc.banner.body')}
        </div>
      </div>

      <div className="card card-pad">
        <div className="spread" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('poc.heading')}{' '}
            <span className="pill pill-warning" style={{ marginLeft: 6 }} data-testid="poc-frozen-pill">
              {t('poc.frozenPill')}
            </span>
          </h3>
          {archive?.poc_shutdown_at && (
            <span className="muted" style={{ fontSize: 12 }}>
              {t('poc.asOf', {
                date: formatDate(archive.poc_shutdown_at, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }),
              })}
            </span>
          )}
        </div>

        {isLoading ? (
          <div
            className="skeleton"
            style={{ height: 140, marginTop: 14 }}
            aria-busy="true"
            aria-label={t('poc.loading')}
          />
        ) : isError || !archive ? (
          <p className="muted" style={{ fontSize: 13, marginTop: 14 }} data-testid="poc-archive-error">
            {t('poc.error')}
          </p>
        ) : archive.poc_applicable === false ? (
          <p className="muted" style={{ fontSize: 13, marginTop: 14 }} data-testid="poc-not-applicable">
            {t('poc.notApplicable')}
          </p>
        ) : (
          <>
            {/* AC-2: eingefrorene Kacheln (Legacy-Farbgebung) */}
            <PocTiles archive={archive} />

            <h4 className="muted" style={{ fontSize: 12, marginTop: 18, marginBottom: 0 }}>
              {t('poc.lastRewards.heading')}
            </h4>
            <LastRewardsTable rows={archive.last_rewards ?? []} />
          </>
        )}

        {/* AC-3: CSV-Export über den BE-Export-Endpunkt */}
        <div className="htdt-export-row">
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              void handleExport()
            }}
            disabled={exporting || isLoading}
            data-testid="poc-archive-export-button"
          >
            <Download size={14} aria-hidden="true" style={{ marginRight: 6 }} />
            {exporting ? t('poc.export.pending') : t('poc.export.button')}
          </button>
          <span className="muted" style={{ fontSize: 11 }}>
            {t('poc.export.hint')}
          </span>
        </div>
      </div>
    </div>
  )
}
